  <template>
    <div v-if="isOpen" class="cart-modal-overlay" @click.self="closeModal">
      <aside class="cart-modal" role="dialog" aria-modal="true">
        <header class="cart-header">
          <div>
            <h2 class="cart-title">Your cart</h2>
            <p class="cart-subtitle">{{ items.length }} item{{ items.length === 1 ? '' : 's' }}</p>
          </div>
          <button class="close-btn" @click="closeModal" aria-label="Close cart">&times;</button>
        </header>

        <div class="cart-body">
          <div v-if="items.length" class="cart-banner">
            <p v-if="isFreeDigital && digitalSelected" class="w-full">
              <span class="banner-title">Digital images</span>
              Complimentary downloads for this {{ eventTerminology ? 'event' : 'ceremony' }}. We'll prepare them once you click
              <span class="font-semibold">{{ primaryButtonLabel }}</span>.
            </p>
            <p v-else-if="showDigitalInfo" class="w-full">
              <span class="banner-title">Digital images</span>
              {{ digitalPriceSummary }}
            </p>
            <p v-else class="w-full">
              <span class="banner-title">Print images</span>
              {{ printPriceSummary }}
            </p>
          </div>

          <div v-else class="empty-state">
            <p class="empty-title">Your cart is empty</p>
            <p class="empty-subtitle">Pick a few snapshots to get started.</p>
          </div>

          <div class="cart-items">
            <div
              v-for="(image, index) in items"
              :key="image.id || image.src || index"
              class="cart-item"
            >
              <img :src="image.src" class="item-thumbnail" alt="Selected snapshot" />
              <div class="item-info">
                <div class="item-title">Snapshot {{ index + 1 }}</div>
                <div v-if="!commerceEnabled" class="item-meta">
                  <span v-if="image.isDigitalSelected" class="pill">Digital</span>
                  <span v-if="image.isPrintSelected" class="pill pill-outline">Print</span>
                </div>
                <div v-else class="item-choice-grid">
                  <label class="item-choice">
                    <input
                      type="checkbox"
                      :checked="image.isDigitalSelected"
                      @change="setItemDigitalSelected(image, $event.target.checked)"
                    >
                    <span>Digital</span>
                  </label>
                  <label class="item-choice">
                    <input
                      type="checkbox"
                      :checked="image.isPrintSelected"
                      @change="setItemPrintSelected(image, index, $event.target.checked)"
                    >
                    <span>Print</span>
                  </label>
                </div>
                <div v-if="showItemPrintNudge(image)" class="item-print-nudge">
                  <span>Add a printed copy from {{ formatMoney(lowestPrintAmount) }}</span>
                  <button type="button" @click="addPrintCopyForItem(image, index)">
                    Add print copy
                  </button>
                </div>
                <button class="remove-link" @click="removeItem(image)">Remove</button>
                <label v-if="commerceEnabled && image.isPrintSelected" class="item-finish-field">
                  <span>Print product</span>
                  <select
                    :value="itemPrintProductCode(index)"
                    :aria-label="`Print product for Snapshot ${index + 1}`"
                    @change="setItemPrintProduct(index, $event.target.value)"
                  >
                    <option
                      v-for="product in printProducts"
                      :key="product.code"
                      :value="product.code"
                    >
                      {{ product.label }} - {{ formatMoney(product.amount) }}
                    </option>
                  </select>
                  <small v-if="itemPrintProduct(index).description" class="print-product-description">
                    {{ itemPrintProduct(index).description }}
                  </small>
                </label>
              </div>
            </div>
          </div>

          <div v-if="commerceEnabled && items.length" class="commerce-panel">
            <div class="commerce-heading">
              <h3>Order summary</h3>
              <span>{{ formattedTotal }}</span>
            </div>
            <div class="summary-lines">
              <div v-if="digitalCount" class="summary-line">
                <span>{{ digitalCount }} digital image{{ digitalCount === 1 ? '' : 's' }}</span>
                <span>{{ formatMoney(digitalAmount) }}</span>
              </div>
              <div v-if="printCount" class="summary-line">
                <span>{{ printCount }} print{{ printCount === 1 ? '' : 's' }}</span>
                <span>{{ formatMoney(printsAmount) }}</span>
              </div>
              <div v-if="deliveryAmount" class="summary-line">
                <span>{{ selectedDeliveryOption.label || 'Delivery' }}</span>
                <span>{{ formatMoney(deliveryAmount) }}</span>
              </div>
            </div>

            <div v-if="showPrintNudge" class="print-nudge">
              <div>
                <p class="print-nudge-title">Add printed copies?</p>
                <p class="print-nudge-copy">
                  Printed copies start from {{ formatMoney(lowestPrintAmount) }} each. Add them only to the images you want, then choose a folder, mount or pack for each one.
                </p>
              </div>
            </div>

            <div v-if="includesPrints" class="option-section">
              <label class="option-label" for="snapshot-delivery-option">Delivery</label>
              <select id="snapshot-delivery-option" v-model="purchase.deliveryMethod" class="option-select">
                <option
                  v-for="option in enabledDeliveryOptions"
                  :key="option.code"
                  :value="option.code"
                >
                  {{ option.label }}{{ option.amount ? ` (+${formatMoney(option.amount)})` : '' }}
                </option>
              </select>
            </div>

            <p v-if="stripeCollectsAddress" class="stripe-address-note">
              Stripe Checkout will collect your delivery address securely before payment.
            </p>

            <div v-if="digitalCount && digitalPolicyNotes.length" class="policy-note">
              <p class="policy-note-title">Digital image terms</p>
              <ul>
                <li v-for="note in digitalPolicyNotes" :key="note">{{ note }}</li>
              </ul>
            </div>

            <div v-if="includesPrints && customerPolicyNotes.length" class="policy-note">
              <p class="policy-note-title">Print delivery terms</p>
              <ul>
                <li v-for="note in customerPolicyNotes" :key="note">{{ note }}</li>
              </ul>
            </div>

            <p v-if="showPolicyLink" class="policy-link">
              Full terms:
              <a :href="commerceTermsUrl" target="_blank" rel="noopener">
                Terms and conditions
              </a>
            </p>

            <p v-if="checkoutError" class="checkout-error">{{ checkoutError }}</p>
          </div>
        </div>

        <footer class="cart-footer">
          <div class="summary-row" v-if="commerceEnabled && items.length">
            <span>Total</span>
            <span class="summary-total">{{ formattedTotal }}</span>
          </div>
          <div class="summary-row" v-else-if="digitalSelected && !isFreeDigital">
            <span>Total</span>
            <span class="summary-total">{{ isAdelaide ? 'AUD$' : '£' }}{{ cartTotal }}</span>
          </div>
          <p v-else-if="digitalSelected && isFreeDigital" class="summary-free">
            Digital images are free for this {{ eventTerminology ? 'event' : 'ceremony' }}.
          </p>
          <button class="primary-btn" @click="purchaseButtonClicked">
            {{ primaryButtonLabel }}
          </button>
          <button
            v-if="items.length > 0 && !this.isSharedBasket()"
            class="secondary-btn"
            @click="shareButtonClicked"
          >
            {{ shareBasketText }}
          </button>
          <p class="fine-print">
            {{ commerceEnabled
              ? 'You will receive an order reference after checkout. Delivery and collection options are confirmed before payment.'
              : `Note: Print purchases can be made on ${eventTerminology ? 'event' : 'graduation'} day at our photo desk. You will receive a reference code for the images selected here.` }}
          </p>
        </footer>

        <div v-if="activeCheckoutUpsells.length" class="checkout-upsell-overlay" @click.self="closeCheckoutUpsell">
          <section class="checkout-upsell" role="dialog" aria-modal="true" aria-labelledby="checkout-upsell-title">
            <p class="checkout-upsell-kicker">Before checkout</p>
            <p v-if="checkoutUpsellStepLabel" class="checkout-upsell-step">{{ checkoutUpsellStepLabel }}</p>
            <p id="checkout-upsell-title" class="checkout-upsell-title">{{ currentCheckoutUpsell.title }}</p>
            <p class="checkout-upsell-copy">{{ currentCheckoutUpsell.body }}</p>

            <div v-if="currentCheckoutUpsell.type === 'choose_more'" class="checkout-upsell-actions">
              <button class="upsell-primary-action" type="button" @click="chooseMoreImagesFromUpsell">
                Choose more images
              </button>
            </div>

            <div v-else-if="currentCheckoutUpsell.type === 'print_items'" class="checkout-upsell-items">
              <div
                v-for="index in currentCheckoutUpsell.itemIndexes"
                :key="`print-upsell-${index}`"
                class="checkout-upsell-item"
              >
                <img :src="items[index].src" alt="Selected snapshot" />
                <span>Snapshot {{ index + 1 }}</span>
                <button
                  v-if="canAddPrintCopy(items[index])"
                  type="button"
                  @click="addPrintCopyForItem(items[index], index)"
                >
                  Add print copy
                </button>
                <label v-else-if="items[index].isPrintSelected" class="checkout-upsell-select">
                  <span>Print product</span>
                  <select
                    :value="itemPrintProductCode(index)"
                    :aria-label="`Print product for Snapshot ${index + 1}`"
                    @change="setItemPrintProduct(index, $event.target.value)"
                  >
                    <option
                      v-for="product in printProducts"
                      :key="product.code"
                      :value="product.code"
                    >
                      {{ product.label }} - {{ formatMoney(product.amount) }}
                    </option>
                  </select>
                </label>
              </div>
            </div>

            <div v-else-if="currentCheckoutUpsell.type === 'digital_items'" class="checkout-upsell-items">
              <div
                v-for="index in currentCheckoutUpsell.itemIndexes"
                :key="`digital-upsell-${index}`"
                class="checkout-upsell-item"
              >
                <img :src="items[index].src" alt="Selected snapshot" />
                <span>Snapshot {{ index + 1 }}</span>
                <button
                  v-if="canAddDigitalCopy(items[index])"
                  type="button"
                  @click="addDigitalCopyForItem(items[index])"
                >
                  Add digital copy
                </button>
              </div>
            </div>

            <div v-else-if="currentCheckoutUpsell.type === 'delivery'" class="delivery-choice-grid">
              <button
                v-for="option in enabledDeliveryOptions"
                :key="option.code"
                type="button"
                class="delivery-choice-card"
                :class="{ selected: purchase.deliveryMethod === option.code }"
                @click="setDeliveryMethod(option.code)"
              >
                <span>{{ option.label }}</span>
                <strong>{{ option.amount ? `+${formatMoney(option.amount)}` : 'Included' }}</strong>
              </button>
              <p v-if="stripeCollectsAddress" class="delivery-choice-note">
                Stripe Checkout will collect your delivery address securely before payment.
              </p>
              <div class="delivery-choice-total">
                <span>Total with delivery</span>
                <strong>{{ formattedTotal }}</strong>
              </div>
            </div>

            <button
              v-if="hasNextCheckoutUpsell"
              class="upsell-primary-action checkout-upsell-next"
              type="button"
              @click="nextCheckoutUpsell"
            >
              Next option
            </button>
            <button class="upsell-secondary-action checkout-upsell-continue" type="button" @click="continueAfterCheckoutUpsell">
              Continue to checkout
            </button>
          </section>
        </div>
      </aside>

      <ShareBasketFormModal
        v-if="isShareBasketModalVisible"
        @submitModal="submitFormModal"
        @close="hideFormModal"
      />

      <QRCodeShareModal
        v-if="isQRCodeShareModalVisible"
        :shareBasketUrl="basketUrl"
        @submitModal="submitFormModal"
        @close="hideQRCodeModal"
      />

    </div>
  </template>
  
  <script>
  import QRCodeShareModal from './QRCodeShareModal.vue';
  import ShareBasketFormModal from './ShareBasketFormModal.vue';
  import { extend } from 'vee-validate';
  import { getSnapshotConfig } from '../../snapshotConfig';
  import {
    required,
    min,
    max,
    email,
    regex,
    confirmed
  } from "vee-validate/dist/rules"

  extend("personal_email", {
    message: (field) =>
      "The " +
      field +
      " value is not a personal email, please enter an email not ending in ac.uk.",
    validate: (value) => !/^.*((\.ac\.uk)|(bpp\.com))$/.test(value),
  });

  extend("required", {
    ...required,
    message: "This field is required",
  });
  extend("min", min);
  extend("confirmed", {
    ...confirmed,
    message: "Your email addresses do not match",
  });
  extend("regex", regex);
  extend("max", max);
  extend("email", email);

  export default {
    name: "CartModal",
  props: {
    isOpen: { type: Boolean, default: false },
    items: { type: Array, default: () => [] },
    basketUrl: String,
    isFreeDigital: { type: Boolean, default: false },
  },
    components: {
      ShareBasketFormModal,
      QRCodeShareModal,
    },
    data() {
      return {
        isShareBasketModalVisible: false,
        isQRCodeShareModalVisible: false,
        shareBasketText: 'Share your Basket',
        checkoutError: '',
        activeCheckoutUpsells: [],
        activeCheckoutUpsellIndex: 0,
        dismissedCheckoutUpsellIds: [],
        purchase: {
          itemOptions: [],
          deliveryMethod: 'uk_delivery',
        },
        form: {
          name: "",
          email: null,
        }
      }
    },
    watch: {
      basketUrl(url) {
        this.newBasketShareLink(url);
      }
    },
   
    computed: {
      snapshotConfig() {
        return getSnapshotConfig(this.$route.params.uni);
      },
      isAdelaide() {
        return this.snapshotConfig.pricingTier === 'aud';
      },
      eventTerminology() {
        return Boolean(this.snapshotConfig.eventTerminology) && !this.snapshotConfig.isHeadshot;
      },
      commerce() {
        return this.snapshotConfig.commerce || {};
      },
      commerceEnabled() {
        return Boolean(this.commerce.enabled);
      },
      currencySymbol() {
        const currency = this.commerce.currency || (this.isAdelaide ? 'aud' : 'gbp');
        if (currency === 'aud') return 'A$';
        if (currency === 'eur') return '€';
        if (currency === 'usd') return '$';
        return '£';
      },
      printProducts() {
        return Array.isArray(this.commerce.printProducts) ? this.commerce.printProducts : [];
      },
      enabledDeliveryOptions() {
        const options = Array.isArray(this.commerce.deliveryOptions) ? this.commerce.deliveryOptions : [];
        return options.filter((option) => option.enabled);
      },
      selectedDeliveryOption() {
        return this.enabledDeliveryOptions.find((option) => option.code === this.purchase.deliveryMethod) || this.enabledDeliveryOptions[0] || { amount: 0, code: 'none', type: 'shipping', requiresAddress: true };
      },
      digitalPriceSummary() {
        if (!this.commerceEnabled) {
          return `1st image: ${this.isAdelaide ? 'AUD$40' : '£20'}. Additional: ${this.isAdelaide ? 'AUD$30' : '£15'} each.`;
        }
        return `Digital bundles: 1 image ${this.formatMoney(1600)}, 2 images ${this.formatMoney(3200)}, 3-5 images ${this.formatMoney(4500)}, 6+ images ${this.formatMoney(7500)}.`;
      },
      printPriceSummary() {
        if (!this.commerceEnabled) {
          return `From ${this.isAdelaide ? 'AUD$30' : '£15'} each. Frames available at checkout on the day.`;
        }
        if (!this.printProducts.length) return 'Print products are shown at checkout.';
        const lowest = Math.min(...this.printProducts.map((product) => Number(product.amount || 0)));
        return `Print products from ${this.formatMoney(lowest)}. Choose the print product for each image at checkout.`;
      },
      customerPolicyNotes() {
        const notes = this.commerce.customerPolicyNotes;
        return Array.isArray(notes) ? notes.filter(Boolean) : [];
      },
      digitalPolicyNotes() {
        const notes = this.commerce.digitalPolicyNotes;
        return Array.isArray(notes) ? notes.filter(Boolean) : [];
      },
      commerceTermsUrl() {
        return this.commerce.termsUrl || '';
      },
      showPolicyLink() {
        return Boolean(this.commerceTermsUrl && (this.digitalCount || this.includesPrints));
      },
      includesPrints() {
        return this.printCount > 0;
      },
      requiresDeliveryAddress() {
        return this.includesPrints && Boolean(this.selectedDeliveryOption.requiresAddress);
      },
      stripeCollectsAddress() {
        return this.requiresDeliveryAddress && this.commerceTotal > 0;
      },
      printsAmount() {
        if (!this.items.length) return 0;
        return this.items.reduce((total, item, index) => {
          if (!item.isPrintSelected) return total;
          return total + Number(this.itemPrintProduct(index).amount || 0);
        }, 0);
      },
      digitalAmount() {
        if (!this.commerceEnabled || this.isFreeDigital) return 0;
        const count = this.digitalCount;
        if (!count) return 0;
        if (count === 1) return 1600;
        if (count === 2) return 3200;
        if (count <= 5) return 4500;
        return 7500;
      },
      deliveryAmount() {
        return this.includesPrints ? Number(this.selectedDeliveryOption.amount || 0) : 0;
      },
      commerceTotal() {
        return this.digitalAmount + this.printsAmount + this.deliveryAmount;
      },
      formattedTotal() {
        return this.formatMoney(this.commerceTotal);
      },
      showDigitalInfo() {
        if (typeof this.$route.query.printOnly === undefined) {
          return true;
        }
        else {
          if (this.$route.query.printOnly == "true") {
            return false;
          }
          else {
            return true;
          }
        } 
      },
      isOnlyPrints() {
        return this.items.every(item => item.isPrintSelected && !item.isDigitalSelected)
      },
      digitalSelected() {
        return this.items.some(item => item.isDigitalSelected)
      },
      digitalCount() {
        return this.items.filter(item => item.isDigitalSelected).length;
      },
      printCount() {
        return this.items.filter(item => item.isPrintSelected).length;
      },
      printNudgeItemCount() {
        return this.items.filter(item => item.isDigitalSelected && !item.isPrintSelected).length;
      },
      lowestPrintAmount() {
        if (!this.printProducts.length) return 0;
        return Math.min(...this.printProducts.map((product) => Number(product.amount || 0)));
      },
      showPrintNudge() {
        return this.commerceEnabled && this.printProducts.length > 0 && this.printNudgeItemCount > 0;
      },
      cartTotal() {
        var digitals_selected = 0
        // Legacy non-commerce pricing path.
        this.items.forEach((img) => {
          if (img.isDigitalSelected) {
            digitals_selected += 1;
          }
        });
        if (digitals_selected == 0) {
          return 0;
        }

        if (this.isFreeDigital) {
          return 0;
        }

        if (this.isAdelaide) {
          // First digital = AUD$40, subsequent = AUD$30
          return ((digitals_selected - 1) * 30) + (40);
        } else {
          return ((digitals_selected - 1) * 15) + (20);
        }
      },
      primaryButtonLabel() {
        if (this.activeCheckoutUpsells.length) {
          return 'Continue to checkout';
        }
        if (this.commerceEnabled && this.commerceTotal > 0) {
          return 'Checkout';
        }
        if (this.commerceEnabled && this.includesPrints) {
          return 'Submit order';
        }
        if (this.isOnlyPrints) {
          return 'Submit selection and pay in person';
        }
        if (this.isFreeDigital) {
          return 'Download my images';
        }
        return 'Pay Online for Digital';
      },
      currentCheckoutUpsell() {
        return this.activeCheckoutUpsells[this.activeCheckoutUpsellIndex] || {};
      },
      hasNextCheckoutUpsell() {
        return this.activeCheckoutUpsellIndex < this.activeCheckoutUpsells.length - 1;
      },
      checkoutUpsellStepLabel() {
        if (this.activeCheckoutUpsells.length <= 1) return '';
        return `Option ${this.activeCheckoutUpsellIndex + 1} of ${this.activeCheckoutUpsells.length}`;
      },
    },
    methods: {
      formatMoney(amount) {
        return `${this.currencySymbol}${(Number(amount || 0) / 100).toFixed(2)}`;
      },
      ensureDefaultPurchase() {
        if (!Array.isArray(this.purchase.itemOptions)) {
          this.$set(this.purchase, 'itemOptions', []);
        }
        this.items.forEach((item, index) => {
          const existing = this.purchase.itemOptions[index] || {};
          const fallbackPrintProduct = this.printProducts[0]?.code || '';
          const nextPrintProduct = this.printProducts.find((product) => product.code === existing.printProduct)
            ? existing.printProduct
            : fallbackPrintProduct;
          const nextOption = {
            ...existing,
            digitalSelected: Boolean(item.isDigitalSelected),
            printSelected: Boolean(item.isPrintSelected),
            printProduct: nextPrintProduct,
          };
          if (!this.purchase.itemOptions[index]) {
            this.$set(this.purchase.itemOptions, index, nextOption);
            return;
          }
          const changed = (
            existing.digitalSelected !== nextOption.digitalSelected ||
            existing.printSelected !== nextOption.printSelected ||
            existing.printProduct !== nextOption.printProduct
          );
          if (changed) {
            this.$set(this.purchase.itemOptions, index, nextOption);
          }
        });
        if (this.purchase.itemOptions.length > this.items.length) {
          this.purchase.itemOptions.splice(this.items.length);
        }
        if (!this.enabledDeliveryOptions.find((option) => option.code === this.purchase.deliveryMethod) && this.enabledDeliveryOptions[0]) {
          this.purchase.deliveryMethod = this.enabledDeliveryOptions[0].code;
        }
      },
      validatePurchase() {
        this.ensureDefaultPurchase();
        if (!this.items.some((item) => item.isDigitalSelected || item.isPrintSelected)) {
          this.checkoutError = 'Choose digital, print, or both for at least one image.';
          return false;
        }
        if (this.requiresDeliveryAddress && !this.stripeCollectsAddress) {
          this.checkoutError = 'Stripe Checkout is required to collect delivery details.';
          return false;
        }
        this.checkoutError = '';
        return true;
      },
      purchasePayload() {
        if (!this.commerceEnabled) return null;
        this.ensureDefaultPurchase();
        const purchase = JSON.parse(JSON.stringify(this.purchase));
        purchase.mode = 'per_item';
        purchase.itemOptions = purchase.itemOptions.slice(0, this.items.length);
        delete purchase.deliveryAddress;
        return purchase;
      },
      itemPrintProductCode(index) {
        return this.purchase.itemOptions[index]?.printProduct || this.printProducts[0]?.code || '';
      },
      itemPrintProduct(index) {
        const code = this.purchase.itemOptions?.[index]?.printProduct || '';
        return this.printProducts.find((product) => product.code === code) || this.printProducts[0] || { amount: 0, code: '', label: 'Printed photo' };
      },
      setItemPrintProduct(index, code) {
        this.ensureDefaultPurchase();
        this.$set(this.purchase.itemOptions, index, {
          ...(this.purchase.itemOptions[index] || {}),
          printProduct: code,
        });
        this.checkoutError = '';
      },
      setItemDigitalSelected(image, checked) {
        this.$set(image, 'isDigitalSelected', checked);
        this.checkoutError = '';
        this.activeCheckoutUpsells = [];
        this.activeCheckoutUpsellIndex = 0;
        this.ensureDefaultPurchase();
      },
      setItemPrintSelected(image, index, checked) {
        this.$set(image, 'isPrintSelected', checked);
        if (checked && !this.purchase.itemOptions[index]?.printProduct && this.printProducts[0]) {
          this.setItemPrintProduct(index, this.printProducts[0].code);
        }
        this.checkoutError = '';
        this.activeCheckoutUpsells = [];
        this.activeCheckoutUpsellIndex = 0;
        this.ensureDefaultPurchase();
      },
      showItemPrintNudge(image) {
        return this.canAddPrintCopy(image);
      },
      canAddPrintCopy(image) {
        return this.commerceEnabled && this.printProducts.length > 0 && image && image.isDigitalSelected && !image.isPrintSelected;
      },
      canAddDigitalCopy(image) {
        return this.commerceEnabled && image && image.isPrintSelected && !image.isDigitalSelected;
      },
      addPrintCopyForItem(item, index) {
        this.ensureDefaultPurchase();
        this.$set(item, 'isPrintSelected', true);
        const existing = this.purchase.itemOptions[index] || {};
        this.$set(this.purchase.itemOptions, index, {
          ...existing,
          digitalSelected: Boolean(item.isDigitalSelected),
          printSelected: true,
          printProduct: existing.printProduct || this.printProducts[0]?.code || '',
        });
        this.checkoutError = '';
        this.ensureDefaultPurchase();
      },
      addDigitalCopyForItem(item) {
        this.$set(item, 'isDigitalSelected', true);
        this.checkoutError = '';
        this.ensureDefaultPurchase();
      },
      buildCheckoutUpsells() {
        if (!this.commerceEnabled || !this.items.length) return [];
        const isDismissed = (id) => this.dismissedCheckoutUpsellIds.includes(id);
        const upsells = [];
        const printIndexes = this.items
          .map((item, index) => (this.canAddPrintCopy(item) ? index : null))
          .filter((index) => index !== null);
        if (printIndexes.length && !isDismissed('add_print_copies')) {
          upsells.push({
            id: 'add_print_copies',
            type: 'print_items',
            itemIndexes: printIndexes,
            title: 'Want printed copies too?',
            body: `Printed copies start from ${this.formatMoney(this.lowestPrintAmount)} each and can be delivered with your order.`,
          });
        }
        if (this.digitalCount === 2 && !isDismissed('better_digital_bundle')) {
          upsells.push({
            id: 'better_digital_bundle',
            type: 'choose_more',
            title: `Add up to 3 more digital images for ${this.formatMoney(1300)} total`,
            body: `Your current digital images are ${this.formatMoney(3200)}. The 3-5 image bundle is ${this.formatMoney(4500)}.`,
          });
        } else if (this.digitalCount >= 3 && this.digitalCount < 5 && !isDismissed('free_digital_pack_slots')) {
          const remaining = 5 - this.digitalCount;
          upsells.push({
            id: 'free_digital_pack_slots',
            type: 'choose_more',
            title: `Add ${remaining} more digital image${remaining === 1 ? '' : 's'} at no extra cost`,
            body: `Your ${this.formatMoney(4500)} digital bundle covers up to 5 images.`,
          });
        } else if (this.digitalCount >= 6 && !isDismissed('large_digital_bundle_more')) {
          upsells.push({
            id: 'large_digital_bundle_more',
            type: 'choose_more',
            title: 'You can add more digital images at no extra cost',
            body: `Your ${this.formatMoney(7500)} digital bundle covers 6 or more images.`,
          });
        }
        const digitalIndexes = this.items
          .map((item, index) => (this.canAddDigitalCopy(item) ? index : null))
          .filter((index) => index !== null);
        if (digitalIndexes.length && !isDismissed('add_digital_copies')) {
          upsells.push({
            id: 'add_digital_copies',
            type: 'digital_items',
            itemIndexes: digitalIndexes,
            title: 'Add digital files for your prints?',
            body: 'Keep a downloadable copy of any printed image in the same order.',
          });
        }
        if (this.includesPrints && this.enabledDeliveryOptions.length && !isDismissed('confirm_delivery')) {
          upsells.push({
            id: 'confirm_delivery',
            type: 'delivery',
            title: 'Delivery for your prints',
            body: 'Choose how your printed photos should be delivered before checkout.',
          });
        }
        return upsells;
      },
      maybeShowCheckoutUpsells() {
        const upsells = this.buildCheckoutUpsells();
        if (!upsells.length) return false;
        this.activeCheckoutUpsells = upsells;
        this.activeCheckoutUpsellIndex = 0;
        this.checkoutError = '';
        return true;
      },
      dismissCheckoutUpsell(upsell) {
        if (upsell && !this.dismissedCheckoutUpsellIds.includes(upsell.id)) {
          this.dismissedCheckoutUpsellIds.push(upsell.id);
        }
      },
      dismissActiveCheckoutUpsells() {
        this.activeCheckoutUpsells.forEach((upsell) => {
          if (!this.dismissedCheckoutUpsellIds.includes(upsell.id)) {
            this.dismissedCheckoutUpsellIds.push(upsell.id);
          }
        });
        this.activeCheckoutUpsells = [];
        this.activeCheckoutUpsellIndex = 0;
      },
      nextCheckoutUpsell() {
        this.dismissCheckoutUpsell(this.currentCheckoutUpsell);
        if (this.hasNextCheckoutUpsell) {
          this.activeCheckoutUpsellIndex += 1;
          return;
        }
        this.activeCheckoutUpsells = [];
        this.activeCheckoutUpsellIndex = 0;
      },
      chooseMoreImagesFromUpsell() {
        this.dismissCheckoutUpsell(this.currentCheckoutUpsell);
        this.activeCheckoutUpsells = [];
        this.activeCheckoutUpsellIndex = 0;
        this.closeModal();
      },
      closeCheckoutUpsell() {
        this.activeCheckoutUpsells = [];
        this.activeCheckoutUpsellIndex = 0;
      },
      setDeliveryMethod(code) {
        this.purchase.deliveryMethod = code;
        this.checkoutError = '';
        this.ensureDefaultPurchase();
      },
      submitPurchase() {
        if(this.isSharedBasket()){
          // Get name and Email
          this.isShareBasketModalVisible = true;
        } else {
          // Good to submit
          this.$emit("submit", this.items, null, this.purchasePayload());
        }
      },
      continueAfterCheckoutUpsell() {
        if (this.currentCheckoutUpsell.id !== 'confirm_delivery') {
          const refreshedUpsells = this.buildCheckoutUpsells();
          const deliveryUpsell = refreshedUpsells.find((upsell) => upsell.id === 'confirm_delivery');
          if (deliveryUpsell && !this.dismissedCheckoutUpsellIds.includes('confirm_delivery')) {
            this.activeCheckoutUpsells = [deliveryUpsell];
            this.activeCheckoutUpsellIndex = 0;
            return;
          }
        }
        this.dismissActiveCheckoutUpsells();
        this.ensureDefaultPurchase();
        if(this.commerceEnabled && !this.validatePurchase()){
          return;
        }
        this.submitPurchase();
      },
      closeModal() {
        this.$emit("close");
        this.shareBasketText = 'Share your basket'
      },
      removeItem(image) {
        this.activeCheckoutUpsells = [];
        this.activeCheckoutUpsellIndex = 0;
        this.$emit("remove", image);
      },
      checkItemsBeforeEmmitting(items) {
        let errors = 0;
        items.forEach((item) => {
          // Check if delivery method have been selected
          if(item.isDigitalSelected == false && item.isPrintSelected == false){
            errors++
          }
        });
        return errors
      },      
      isSharedBasket(){
        return !!this.$route.query.shareBasket;
      },
      purchaseButtonClicked() {
        this.ensureDefaultPurchase();
        let errorsCaught = this.checkItemsBeforeEmmitting(this.items);
        if(!this.commerceEnabled && errorsCaught >= 1){
          return
        }
        if(this.commerceEnabled && !this.validatePurchase()){
          return
        }
        if(this.activeCheckoutUpsells.length){
          this.continueAfterCheckoutUpsell();
          return;
        }
        if(this.commerceEnabled && this.maybeShowCheckoutUpsells()){
          return;
        }
        this.submitPurchase();
     
      },
      shareButtonClicked() {
        this.$emit("share", this.items)
        // Always open QR modal when share btn is clicked
        this.isQRCodeShareModalVisible = true;
      },
      newBasketShareLink(url){
        this.basketUrl = url;
        // Copy to clipboard
        if(navigator.clipboard){
          // TODO: User selecting share with the same items can be problematic
          // shareBasketText will not update
          navigator.clipboard.writeText(url)
            .then(() =>{
              this.shareBasketText = 'Copied to clipboard';
              setTimeout(() => {
                this.shareBasketText = 'Share your Basket';
              }, 3000);
            })
            .catch(() => {
              this.shareBasketText = 'Failed to generate URL';
            });
        }
      },
      submitFormModal(formData) {
        // Good to submit for checkout
        this.$emit("submit", this.items, formData, this.purchasePayload());
      },
      hideFormModal(){
        this.isShareBasketModalVisible = false;
      },
      hideQRCodeModal(){
        this.isQRCodeShareModalVisible = false;
      }
    },
  };
  </script>
  
  <style scoped>
.cart-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.6);
  display: flex;
  justify-content: flex-end;
  align-items: stretch;
  z-index: 2000;
}

.cart-modal {
  background: #ffffff;
  width: min(480px, 94vw);
  height: 100%;
  border-radius: 16px 0 0 16px;
  box-shadow: -16px 0 40px rgba(15, 23, 42, 0.25);
  display: flex;
  flex-direction: column;
  position: relative;
}

.cart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 20px 12px;
  border-bottom: 1px solid #eef2f7;
}

.cart-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
}

.cart-subtitle {
  font-size: 0.9rem;
  color: #6b7280;
}

.close-btn {
  border: none;
  background: #f1f5f9;
  color: #111827;
  font-size: 22px;
  width: 36px;
  height: 36px;
  border-radius: 999px;
  cursor: pointer;
}

.cart-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px 20px;
}

.cart-banner {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #166534;
  padding: 12px 14px;
  border-radius: 12px;
  font-size: 0.92rem;
  margin-bottom: 16px;
}

.banner-title {
  font-weight: 700;
  display: block;
  margin-bottom: 4px;
}

.empty-state {
  text-align: center;
  padding: 40px 16px 24px;
  color: #6b7280;
}

.empty-title {
  font-weight: 700;
  color: #111827;
}

.empty-subtitle {
  font-size: 0.9rem;
}

.cart-items {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.commerce-panel {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #eef2f7;
}

.commerce-heading {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.commerce-heading h3 {
  font-weight: 700;
  color: #111827;
}

.commerce-heading span {
  font-weight: 800;
  color: #0f766e;
}

.summary-lines {
  display: grid;
  gap: 8px;
  margin-bottom: 12px;
}

.summary-line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  color: #334155;
  font-size: 0.9rem;
}

.checkout-upsell-overlay {
  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.42);
  padding: 20px;
}

.checkout-upsell {
  width: min(420px, 100%);
  max-height: calc(100% - 40px);
  overflow-y: auto;
  border: 1px solid #99f6e4;
  background: #f0fdfa;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.28);
  border-radius: 10px;
  padding: 14px;
}

.checkout-upsell-kicker {
  color: #0f766e;
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0;
  margin-bottom: 3px;
}

.checkout-upsell-title {
  color: #111827;
  font-size: 0.95rem;
  font-weight: 800;
  margin-bottom: 3px;
}

.checkout-upsell-step {
  color: #64748b;
  font-size: 0.78rem;
  font-weight: 700;
  margin-bottom: 6px;
}

.checkout-upsell-copy {
  color: #334155;
  font-size: 0.84rem;
  line-height: 1.35;
}

.checkout-upsell-actions {
  display: grid;
  gap: 8px;
  margin-top: 10px;
}

.checkout-upsell-items {
  display: grid;
  gap: 8px;
  margin-top: 10px;
}

.checkout-upsell-item {
  display: grid;
  grid-template-columns: 44px 1fr auto;
  align-items: center;
  gap: 8px;
  border: 1px solid #ccfbf1;
  background: #ffffff;
  border-radius: 9px;
  padding: 7px;
}

.checkout-upsell-item img {
  width: 44px;
  height: 44px;
  border-radius: 7px;
  object-fit: cover;
  background: #f8fafc;
}

.checkout-upsell-item span {
  color: #111827;
  font-size: 0.82rem;
  font-weight: 700;
}

.checkout-upsell-item button,
.upsell-primary-action,
.upsell-secondary-action {
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 800;
  padding: 8px 10px;
  cursor: pointer;
}

.checkout-upsell-item button,
.upsell-primary-action {
  border: 1px solid #0f766e;
  background: #0f766e;
  color: #ffffff;
}

.checkout-upsell-item button:hover,
.upsell-primary-action:hover {
  background: #0d6d66;
  border-color: #0d6d66;
}

.checkout-upsell-select {
  display: grid;
  gap: 4px;
}

.checkout-upsell-select span {
  color: #475569;
  font-size: 0.72rem;
  font-weight: 800;
}

.checkout-upsell-select select {
  width: 100%;
  min-width: 150px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #ffffff;
  color: #111827;
  font-size: 0.78rem;
  padding: 7px 9px;
}

.upsell-secondary-action {
  border: 1px solid #0f766e;
  background: #ffffff;
  color: #0f766e;
}

.checkout-upsell-continue {
  width: 100%;
  margin-top: 12px;
}

.checkout-upsell-next {
  width: 100%;
  margin-top: 12px;
}

.delivery-choice-grid {
  display: grid;
  gap: 8px;
  margin-top: 12px;
}

.delivery-choice-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  border: 1px solid #ccfbf1;
  background: #ffffff;
  color: #111827;
  border-radius: 9px;
  padding: 11px 12px;
  text-align: left;
  cursor: pointer;
}

.delivery-choice-card.selected {
  border-color: #0f766e;
  box-shadow: 0 0 0 2px rgba(15, 118, 110, 0.14);
}

.delivery-choice-card span {
  font-size: 0.86rem;
  font-weight: 800;
}

.delivery-choice-card strong {
  color: #0f766e;
  font-size: 0.86rem;
  font-weight: 900;
  white-space: nowrap;
}

.delivery-choice-note {
  border: 1px solid #bfdbfe;
  background: #eff6ff;
  color: #1e3a8a;
  border-radius: 9px;
  font-size: 0.82rem;
  font-weight: 700;
  line-height: 1.35;
  padding: 9px 10px;
}

.delivery-choice-total {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  color: #111827;
  font-size: 0.9rem;
  font-weight: 800;
  padding-top: 2px;
}

.delivery-choice-total strong {
  color: #0f766e;
  font-size: 1rem;
}

.print-nudge {
  border: 1px solid #fed7aa;
  background: #fff7ed;
  color: #7c2d12;
  border-radius: 10px;
  padding: 12px;
  margin: 10px 0 12px;
}

.print-nudge-title {
  color: #111827;
  font-size: 0.92rem;
  font-weight: 800;
  margin-bottom: 2px;
}

.print-nudge-copy {
  font-size: 0.82rem;
  line-height: 1.35;
}

.package-grid {
  display: grid;
  gap: 10px;
}

.package-card {
  text-align: left;
  border: 1px solid #dbe3ea;
  background: #ffffff;
  border-radius: 12px;
  padding: 12px;
  display: grid;
  gap: 4px;
  cursor: pointer;
}

.package-card.selected {
  border-color: #0f766e;
  box-shadow: 0 0 0 2px rgba(15, 118, 110, 0.14);
}

.package-card.disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.package-title {
  font-weight: 700;
  color: #111827;
}

.package-copy {
  font-size: 0.84rem;
  color: #64748b;
}

.package-price {
  font-weight: 800;
  color: #0f766e;
}

.option-section {
  margin-top: 12px;
}

.option-label {
  display: block;
  font-size: 0.78rem;
  font-weight: 700;
  color: #475569;
  margin-bottom: 4px;
}

.option-select {
  width: 100%;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 10px 12px;
  background: #ffffff;
  color: #111827;
}

.stripe-address-note {
  margin-top: 12px;
  border: 1px solid #bfdbfe;
  border-radius: 10px;
  background: #eff6ff;
  color: #1e3a8a;
  font-size: 0.86rem;
  font-weight: 600;
  padding: 10px 12px;
}

.policy-note {
  margin-top: 10px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #f8fafc;
  color: #334155;
  font-size: 0.82rem;
  padding: 10px 12px;
}

.policy-note-title {
  color: #111827;
  font-weight: 700;
  margin-bottom: 6px;
}

.policy-note ul {
  display: grid;
  gap: 4px;
  list-style: disc;
  margin: 0;
  padding-left: 16px;
}

.policy-link {
  margin-top: 8px;
  color: #475569;
  font-size: 0.82rem;
}

.policy-link a {
  color: #0f766e;
  font-weight: 700;
  text-decoration: underline;
}

.checkout-error {
  margin-top: 10px;
  color: #b91c1c;
  font-size: 0.86rem;
  font-weight: 600;
}

.cart-item {
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 10px;
  border: 1px solid #eef2f7;
  border-radius: 12px;
  background: #ffffff;
}

.item-thumbnail {
  width: 82px;
  height: 82px;
  object-fit: cover;
  border-radius: 10px;
  background: #f8fafc;
}

.item-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.item-title {
  font-weight: 600;
  color: #111827;
}

.item-meta {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.item-choice-grid {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.item-choice {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 32px;
  color: #334155;
  font-size: 0.86rem;
  font-weight: 700;
}

.item-choice input {
  width: 16px;
  height: 16px;
  accent-color: #0f766e;
}

.item-print-nudge {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 8px;
  border: 1px solid #fed7aa;
  background: #fff7ed;
  border-radius: 9px;
  padding: 8px 9px;
}

.item-print-nudge span {
  color: #7c2d12;
  font-size: 0.78rem;
  font-weight: 700;
  line-height: 1.3;
}

.item-print-nudge button {
  border: 1px solid #c2410c;
  background: #c2410c;
  color: #ffffff;
  font-size: 0.78rem;
  font-weight: 800;
  border-radius: 8px;
  padding: 7px 9px;
  cursor: pointer;
  white-space: nowrap;
}

.item-print-nudge button:hover {
  background: #9a3412;
  border-color: #9a3412;
}

.item-finish-field {
  display: grid;
  gap: 4px;
  margin-top: 2px;
}

.item-finish-field span {
  font-size: 0.72rem;
  font-weight: 700;
  color: #475569;
}

.item-finish-field select {
  width: 100%;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  color: #111827;
  background: #ffffff;
  padding: 7px 9px;
  font-size: 0.84rem;
}

.print-product-description {
  display: block;
  color: #64748b;
  font-size: 0.78rem;
  font-weight: 500;
  line-height: 1.35;
}

.pill {
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  background: #ecfeff;
  color: #0e7490;
}

.pill-outline {
  background: #f8fafc;
  color: #1f2937;
  border: 1px solid #e2e8f0;
}

.remove-link {
  background: transparent;
  border: none;
  color: #ef4444;
  font-weight: 600;
  font-size: 0.85rem;
  padding: 0;
  cursor: pointer;
  align-self: flex-start;
}

.cart-footer {
  padding: 16px 20px 20px;
  border-top: 1px solid #eef2f7;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  font-weight: 600;
  color: #111827;
}

.summary-total {
  font-size: 1.1rem;
}

.summary-free {
  color: #166534;
  font-weight: 600;
}

.primary-btn {
  background: #0f766e;
  color: #ffffff;
  font-weight: 700;
  border: none;
  border-radius: 12px;
  padding: 12px 16px;
  cursor: pointer;
}

.primary-btn:hover {
  background: #0d6d66;
}

.secondary-btn {
  background: #ffffff;
  color: #0f766e;
  border: 1px solid #0f766e;
  font-weight: 600;
  border-radius: 12px;
  padding: 10px 14px;
  cursor: pointer;
}

.fine-print {
  font-size: 0.78rem;
  color: #6b7280;
  line-height: 1.3;
}

@media (max-width: 640px) {
  .cart-modal {
    width: 100%;
    border-radius: 0;
  }

  .item-print-nudge {
    grid-template-columns: 1fr;
  }

  .item-print-nudge button {
    width: 100%;
  }

  .checkout-upsell-actions {
    grid-template-columns: 1fr;
  }

  .checkout-upsell-item {
    grid-template-columns: 52px 1fr;
  }

  .checkout-upsell-item button,
  .checkout-upsell-select {
    grid-column: 1 / -1;
  }

  .checkout-upsell-item button {
    width: 100%;
  }

  .checkout-upsell-select select {
    min-width: 0;
  }
}
  </style>
  
